import { createClient } from '@/lib/supabase/client';

const supabase = createClient();
import { Database } from '@/types/database.types';

type Job = Database['public']['Tables']['jobs']['Row'];
type Company = Database['public']['Tables']['companies']['Row'];
type JobCategory = Database['public']['Tables']['job_categories']['Row'];

export const jobService = {
  async getFeaturedJobs(limit: number = 5) {
    const { data, error } = await supabase
      .from('jobs')
      .select(`
        id,
        title,
        description,
        location,
        salary_min,
        salary_max,
        salary_currency,
        job_type,
        experience_level,
        requirements,
        benefits,
        status,
        featured,
        created_at,
        expires_at,
        companies!jobs_company_id_fkey(
          name,
          logo_url
        )
      `)
      .eq('status', 'approved')
      .eq('featured', true)
      .order('created_at', { ascending: false })
      .limit(limit);
    
    if (error) throw error;
    
    // Transform data to match expected structure
    const transformedData = data?.map(job => ({
      ...job,
      company: job.companies || {}
    }));
    
    return transformedData || [];
  },

  async getAllApprovedJobs() {
    const { data, error } = await supabase
      .from('jobs')
      .select(`
        *,
        companies!jobs_company_id_fkey(name, logo_url, location),
        job_categories(name)
      `)
      .eq('status', 'approved')
      .order('created_at', { ascending: false });
    
    return { data, error };
  },

  async getJobById(id: string) {
    const { data, error } = await supabase
      .from('jobs')
      .select(`
        *,
        companies!jobs_company_id_fkey(
          name,
          logo_url,
          location,
          description,
          website,
          verification_status
        ),
        job_categories(name)
      `)
      .eq('id', id)
      .single();
    
    return { data, error };
  },

  async getUserJobs(userId: string) {
    const { data, error } = await supabase
      .from('jobs')
      .select(`
        *,
        companies(
          id,
          name,
          logo_url
        ),
        job_categories(
          id,
          name
        )
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    return data;
  },

  async searchJobs(filters: {
    keyword?: string;
    location?: string;
    jobType?: string;
    experienceLevel?: string;
    categoryId?: string;
    minSalary?: number;
    maxSalary?: number;
  }) {
    let query = supabase
      .from('jobs')
      .select(`
        *,
        companies!jobs_company_id_fkey(name, logo_url, location),
        job_categories(name)
      `)
      .eq('status', 'approved');
    
    if (filters.keyword) {
      query = query.or(`title.ilike.%${filters.keyword}%,description.ilike.%${filters.keyword}%`);
    }
    if (filters.location) {
      query = query.ilike('location', `%${filters.location}%`);
    }
    if (filters.jobType) {
      query = query.eq('job_type', filters.jobType);
    }
    if (filters.experienceLevel) {
      query = query.eq('experience_level', filters.experienceLevel);
    }
    if (filters.categoryId) {
      query = query.eq('category_id', filters.categoryId);
    }
    if (filters.minSalary) {
      query = query.gte('salary_min', filters.minSalary);
    }
    if (filters.maxSalary) {
      query = query.lte('salary_max', filters.maxSalary);
    }
    
    const { data, error } = await query.order('featured', { ascending: false }).order('created_at', { ascending: false });
    return { data, error };
  },

  async getJobCategories() {
    const { data, error } = await supabase
      .from('job_categories')
      .select('*')
      .eq('is_active', true)
      .order('name', { ascending: true });
    
    return { data, error };
  },

  async incrementViews(jobId: string) {
    const { data: currentJob } = await supabase
      .from('jobs')
      .select('views_count')
      .eq('id', jobId)
      .single();
    
    if (currentJob) {
      const { error } = await supabase
        .from('jobs')
        .update({ views_count: (currentJob.views_count || 0) + 1 })
        .eq('id', jobId);
      
      return { error };
    }
    
    return { error: new Error('Job not found') };
  },

  async createJob(jobData: any) {
    const { data, error } = await supabase
      .from('jobs')
      .insert([jobData])
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async updateJob(id: string, updates: any, userId: string) {
    const { data, error } = await supabase
      .from('jobs')
      .update(updates)
      .eq('id', id)
      .eq('user_id', userId)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async deleteJob(id: string, userId: string) {
    const { error } = await supabase
      .from('jobs')
      .delete()
      .eq('id', id)
      .eq('user_id', userId);
    
    if (error) throw error;
    return true;
  },

  async getRelatedJobs(currentJobId: string, categoryId: string | null, location: string | null, limit: number = 3) {
    let query = supabase
      .from('jobs')
      .select(`
        id,
        title,
        location,
        salary_min,
        salary_max,
        salary_currency,
        job_type,
        featured,
        created_at,
        companies!jobs_company_id_fkey(
          name,
          logo_url
        )
      `)
      .eq('status', 'approved')
      .neq('id', currentJobId)
      .limit(limit);

    // Prioritize jobs with same category
    if (categoryId) {
      query = query.eq('category_id', categoryId);
    }

    // Then same location
    if (location && !categoryId) {
      query = query.eq('location', location);
    }

    const { data, error } = await query
      .order('featured', { ascending: false })
      .order('created_at', { ascending: false });

    if (error) {
      // If category-specific query fails or returns nothing, try location-based
      if (categoryId && location) {
        const fallbackQuery = supabase
          .from('jobs')
          .select(`
            id,
            title,
            location,
            salary_min,
            salary_max,
            salary_currency,
            job_type,
            featured,
            created_at,
            companies!jobs_company_id_fkey(
              name,
              logo_url
            )
          `)
          .eq('status', 'approved')
          .neq('id', currentJobId)
          .eq('location', location)
          .limit(limit)
          .order('featured', { ascending: false })
          .order('created_at', { ascending: false });

        const { data: fallbackData, error: fallbackError } = await fallbackQuery;
        return { data: fallbackData, error: fallbackError };
      }
      return { data: null, error };
    }

    // If we got results but less than limit, try to fill with other jobs
    if (data && data.length < limit) {
      const additionalQuery = supabase
        .from('jobs')
        .select(`
          id,
          title,
          location,
          salary_min,
          salary_max,
          salary_currency,
          job_type,
          featured,
          created_at,
          companies!jobs_company_id_fkey(
            name,
            logo_url
          )
        `)
        .eq('status', 'approved')
        .neq('id', currentJobId)
        .limit(limit - data.length)
        .order('featured', { ascending: false })
        .order('created_at', { ascending: false });

      // Exclude already fetched job IDs
      if (data.length > 0) {
        const existingIds = data.map(job => job.id);
        additionalQuery.not('id', 'in', `(${existingIds.join(',')})`);
      }

      const { data: additionalData } = await additionalQuery;
      if (additionalData) {
        return { data: [...data, ...additionalData], error: null };
      }
    }

    return { data, error: null };
  },

  async getJobWithCompanyDetails(id: string) {
    const { data, error } = await supabase
      .from('jobs')
      .select(`
        *,
        companies!jobs_company_id_fkey(
          id,
          name,
          logo_url,
          location,
          description,
          website,
          industry,
          size_range,
          established_year,
          verification_status
        ),
        job_categories(
          id,
          name
        )
      `)
      .eq('id', id)
      .eq('status', 'approved')
      .single();

    return { data, error };
  },

  async getPendingJobs() {
    const { data, error } = await supabase
      .from('jobs')
      .select(`
        *,
        companies!jobs_company_id_fkey(
          name,
          logo_url,
          location,
          verification_status
        ),
        job_categories(name)
      `)
      .in('status', ['pending', 'flagged'])
      .order('created_at', { ascending: true });

    return { data, error };
  },

  async approveJob(jobId: string) {
    const { error } = await supabase
      .from('jobs')
      .update({
        status: 'approved',
        approved_at: new Date().toISOString()
      })
      .eq('id', jobId);

    if (error) return { error };
    return { success: true };
  },

  async rejectJob(jobId: string, reason: string) {
    const { error } = await supabase
      .from('jobs')
      .update({
        status: 'rejected',
        moderation_notes: reason
      })
      .eq('id', jobId);

    if (error) return { error };
    return { success: true };
  },

  async flagJob(jobId: string, reason: string) {
    const { error } = await supabase
      .from('jobs')
      .update({
        status: 'flagged',
        moderation_notes: reason
      })
      .eq('id', jobId);

    if (error) return { error };
    return { success: true };
  },
};